import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { businessInfo, navLinks } from "../data/mockData";

const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
      {/* TOP ROW — phone (L) · centered logo · WhatsApp (R) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-16 sm:h-20 lg:h-24">
          {/* Left: Phone */}
          <a
            href={businessInfo.phoneTel}
            onClick={() => trackContactClick("call", "navbar")}
            data-testid="nav-call-btn"
            className="hidden sm:inline-flex items-center gap-2 text-brand-navy hover:text-brand-blue transition-colors"
          >
            <span className="h-10 w-10 rounded-full bg-brand-blue-soft flex items-center justify-center">
              <Phone className="h-4 w-4 text-brand-blue" />
            </span>
            <span className="text-base lg:text-lg font-bold tracking-tight">
              {businessInfo.phone}
            </span>
          </a>

          {/* Center: Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center shrink-0"
          >
            <img
              src={businessInfo.logoUrl}
              alt="TOFT Gas, Heating & Plumbing"
              className="h-12 sm:h-14 lg:h-16 w-auto object-contain"
            />
          </Link>

          {/* Right: WhatsApp */}
          <a
            href={businessInfo.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContactClick("whatsapp", "navbar")}
            data-testid="nav-whatsapp-btn"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-brand-whatsapp text-white text-sm font-semibold hover:bg-brand-whatsapp-dark transition-colors whitespace-nowrap shadow-sm"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            className="sm:hidden p-2 text-brand-navy"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {/* SECONDARY ROW — centered horizontal menu */}
      <div className="hidden sm:block border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
          <nav
            data-testid="desktop-nav"
            className="flex items-center justify-center gap-1 lg:gap-2 overflow-x-auto scrollbar-hide"
          >
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-3 lg:px-4 py-3.5 text-xs lg:text-sm font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-brand-blue"
                      : "text-brand-navy hover:text-brand-blue"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-[3px] bg-brand-blue rounded-t-full" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="sm:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1 px-4 py-4">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 text-base font-semibold rounded-md ${
                    isActive
                      ? "bg-brand-blue-soft text-brand-blue"
                      : "text-brand-navy hover:bg-slate-50"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={businessInfo.phoneTel}
              className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-brand-navy text-white text-sm font-semibold"
            >
              <Phone className="h-4 w-4" />
              {businessInfo.phone}
            </a>
            <a
              href={businessInfo.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-brand-whatsapp text-white text-sm font-semibold"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Us
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
